import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getHeatPumps, getCustomers, getSites, loadReports, removeReport } from "../lib/sync";
import { getForm } from "../lib/formTemplates";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { QuickAddDialog } from "../components/QuickAddDialog";
import MapLink from "../components/MapLink";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  Thermometer,
  FileText,
  Copy,
  Eye,
  History,
  Trash2,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

const ROWS = [
  ["Manufacturer", "manufacturer"],
  ["Model", "model"],
  ["Serial number", "serial_number"],
  ["Installation date", "installation_date"],
  ["Commissioning date", "commissioning_date"],
  ["Warranty", "warranty_details"],
  ["Indoor unit model", "indoor_unit"],
  ["Indoor unit serial", "indoor_serial_number"],
  ["Outdoor unit model", "outdoor_unit"],
  ["Refrigerant", "refrigerant_type"],
];

export default function HeatPumpDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pump, setPump] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [reports, setReports] = useState([]);
  const [delPump, setDelPump] = useState(false);
  const [delReport, setDelReport] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    const pumps = await getHeatPumps().catch(() => []);
    const p = pumps.find((x) => x.id === id);
    setPump(p);
    if (p) {
      const custs = await getCustomers().catch(() => []);
      setCustomer(custs.find((c) => c.id === p.customer_id));
      const sites = await getSites(p.customer_id).catch(() => []);
      setSite(sites.find((s) => s.id === p.site_id));
      setReports(await loadReports({ heat_pump_id: id }).catch(() => []));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const doDeletePump = async () => {
    setDelPump(false);
    try {
      await api.delete(`/heatpumps/${pump.id}`);
      toast.success("Heat pump and its reports deleted");
      navigate(-1);
    } catch (e) {
      toast.error("Could not delete heat pump");
    }
  };

  const openEdit = () => {
    setEditForm({ ...pump });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/heatpumps/${pump.id}`, { ...editForm });
      toast.success("Heat pump updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error("Could not update heat pump");
    } finally {
      setEditSaving(false);
    }
  };

  const doDeleteReport = async () => {
    const r = delReport;
    setDelReport(null);
    try {
      await removeReport(r.id);
      toast.success("Report deleted");
      load();
    } catch (e) {
      toast.error("Could not delete report");
    }
  };

  if (!pump) return <p className="py-10 text-center text-slate-400">Loading…</p>;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-semibold text-slate-500" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Thermometer className="h-6 w-6" />
            </span>
            <div>
              <h1 className="font-heading text-xl font-extrabold text-slate-900">
                {pump.manufacturer} {pump.model}
              </h1>
              <p className="text-sm text-slate-500">
                {customer?.name} • {site?.name}
              </p>
              {(site?.address || customer?.billing_address) && (
                <MapLink
                  address={site?.address || customer?.billing_address}
                  testid="map-heatpump"
                  className="mt-1 text-xs"
                />
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              onClick={openEdit}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-primary"
              data-testid="edit-heatpump-btn"
              title="Edit heat pump"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={() => setDelPump(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
              data-testid="delete-heatpump-btn"
              title="Delete heat pump"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <dl className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {ROWS.map(([label, k]) =>
            pump[k] ? (
              <div key={k} className="flex justify-between border-b border-slate-100 py-1.5 text-sm">
                <dt className="text-slate-500">{label}</dt>
                <dd className="font-semibold text-slate-800 text-right">{pump[k]}</dd>
              </div>
            ) : null
          )}
        </dl>
        {pump.notes && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-500">{pump.notes}</p>}
      </Card>

      <div className="grid grid-cols-2 gap-2">
        <Button
          className="h-12"
          onClick={() => navigate(`/report/new?type=service_report&customer=${pump.customer_id}&site=${pump.site_id}&pump=${pump.id}`)}
          data-testid="new-report-for-pump"
        >
          <FileText className="mr-2 h-4 w-4" /> Service
        </Button>
        <Button
          variant="outline"
          className="h-12"
          onClick={() => navigate(`/report/new?type=commissioning_report&customer=${pump.customer_id}&site=${pump.site_id}&pump=${pump.id}`)}
          data-testid="new-commissioning-for-pump"
        >
          <FileText className="mr-2 h-4 w-4" /> Commissioning
        </Button>
      </div>

      <section>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
          <History className="h-5 w-5 text-primary" /> Report history
        </h2>
        <div className="space-y-2.5" data-testid="service-history">
          {reports.length === 0 && <p className="text-sm text-slate-400">No service reports yet.</p>}
          {reports.map((r) => (
            <Card key={r.id} className="p-4" data-testid={`history-${r.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-900">{r.service_date || "Draft"}</div>
                  <div className="text-xs text-slate-500">{r.engineer_name} • {r.final_status || "—"}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={r.status === "completed" ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"} variant="secondary">
                    {r.status}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]" data-testid={`history-type-${r.id}`}>
                    {getForm(r.form_type).shortLabel}
                  </Badge>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/report/${r.id}/view`)} data-testid={`history-view-${r.id}`}>
                  <Eye className="mr-1 h-3.5 w-3.5" /> View
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/report/new?duplicate=${r.id}`)} data-testid={`history-duplicate-${r.id}`}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Duplicate
                </Button>
                {user?.role === "admin" && (
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDelReport(r)} data-testid={`history-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <AlertDialog open={delPump} onOpenChange={setDelPump}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this heat pump?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the heat pump and all of its service reports.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeletePump} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-heatpump">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delReport} onOpenChange={(o) => !o && setDelReport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this report?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the {delReport?.service_date || "draft"} report.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteReport} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-report">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuickAddDialog
        type={editOpen ? "pump" : null}
        title="Edit heat pump"
        form={editForm}
        setForm={setEditForm}
        onSave={saveEdit}
        saving={editSaving}
        onClose={() => setEditOpen(false)}
      />
    </div>
  );
}
